import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowRight, CalendarPlus } from "lucide-react";
import { useLocale, formatDate } from "@/i18n/format";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MediaPreview, mediaItemFromPiece } from "@/components/media";
import { postFormatLabel } from "@/lib/post-format-label";
import {
  captionPreview,
  daysFromToday,
  localDayKey,
  type UpcomingPost,
} from "@/lib/upcoming-posts";

const MAX_POSTS = 8;

interface UpNextProps {
  brandSlug: string;
  posts: UpcomingPost[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** What goes out over the next 7 days, one row per post, grouped by day. */
export function HomeUpNext({ brandSlug, posts, isLoading, isError, onRetry }: UpNextProps) {
  const { t } = useTranslation("dashboard");
  const locale = useLocale();
  const shown = posts.slice(0, MAX_POSTS);
  const hidden = posts.length - shown.length;

  const days: Array<{ key: string; date: Date; posts: UpcomingPost[] }> = [];
  for (const post of shown) {
    const date = new Date(post.scheduledFor);
    const key = localDayKey(date);
    const day = days.find((d) => d.key === key);
    if (day) day.posts.push(post);
    else days.push({ key, date, posts: [post] });
  }

  function dayLabel(date: Date): string {
    const diff = daysFromToday(date);
    if (diff <= 0) return t("home.upNext.today");
    if (diff === 1) return t("home.upNext.tomorrow");
    return formatDate(date, locale, { weekday: "long", month: "short", day: "numeric" });
  }

  return (
    <section aria-labelledby="home-up-next" className="rounded-xl border bg-card">
      <header className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <h2 id="home-up-next" className="text-sm font-semibold">
          {t("home.upNext.title")}
        </h2>
        <Link
          to={`/${brandSlug}/calendar`}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {t("home.upNext.viewCalendar")}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </header>

      {isLoading ? (
        <div className="space-y-3 p-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-center justify-between gap-3 p-4">
          <p className="text-sm text-destructive">{t("home.upNext.error")}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("home.upNext.retry")}
          </Button>
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <CalendarPlus className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          </span>
          <p className="text-sm font-medium">{t("home.upNext.emptyTitle")}</p>
          <p className="max-w-xs text-xs text-muted-foreground">{t("home.upNext.emptyDesc")}</p>
          <Button asChild size="sm" className="mt-2">
            <Link to={`/${brandSlug}/quick-post`}>{t("home.upNext.emptyCta")}</Link>
          </Button>
        </div>
      ) : (
        <div className="divide-y">
          {days.map((day) => (
            <div key={day.key} className="px-4 py-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {dayLabel(day.date)}
              </p>
              <ul className="space-y-1">
                {day.posts.map((post) => (
                  <UpNextRow key={post.key} brandSlug={brandSlug} post={post} />
                ))}
              </ul>
            </div>
          ))}
          {hidden > 0 && (
            <Link
              to={`/${brandSlug}/calendar`}
              className="block px-4 py-3 text-center text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              {t("home.upNext.more", { count: hidden })}
            </Link>
          )}
        </div>
      )}
    </section>
  );
}

function UpNextRow({ brandSlug, post }: { brandSlug: string; post: UpcomingPost }) {
  const { t } = useTranslation("dashboard");
  const { t: tCal } = useTranslation("calendar");
  const locale = useLocale();
  const { lead } = post;
  const media = mediaItemFromPiece({
    id: lead.id,
    mediaUrls: lead.mediaUrls,
    thumbnailUrl: lead.thumbnailUrl,
    title: lead.title,
  });
  const caption = captionPreview(lead.caption ?? lead.piece?.caption);
  const time = formatDate(new Date(post.scheduledFor), locale, { hour: "numeric", minute: "2-digit" });

  return (
    <li>
      <Link
        to={`/${brandSlug}/calendar`}
        className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {media ? (
          <MediaPreview item={media} className="h-12 w-12 shrink-0" />
        ) : (
          <span className="h-12 w-12 shrink-0 rounded-lg bg-muted" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium tabular-nums text-foreground">{time}</span>
            {post.hasFailure && (
              <span className="inline-flex items-center gap-1 font-medium text-destructive">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {t("home.upNext.failed")}
              </span>
            )}
          </span>
          <span className="block truncate text-sm">{caption || t("home.upNext.noCaption")}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {post.events.map((event) => (
            <span
              key={event.id}
              title={postFormatLabel(event.platform, event.postType ?? event.piece?.postType, tCal)}
              aria-label={postFormatLabel(event.platform, event.postType ?? event.piece?.postType, tCal)}
              className={
                event.status === "FAILED"
                  ? "rounded-full ring-2 ring-destructive ring-offset-1 ring-offset-card"
                  : undefined
              }
            >
              <PlatformIcon platform={event.platform} size={16} />
            </span>
          ))}
        </span>
      </Link>
    </li>
  );
}
